import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, ClickAwayListener, Paper, Popper, Typography } from "@mui/material";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment";

// Normalise the app locale to a moment locale code.
const momentLocale = (locale) => (locale === "fr-fr" ? "fr" : locale || "en");

const navBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: "transparent",
  color: "hsl(178, 93%, 10%)",
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": { backgroundColor: "hsla(153, 68%, 25%, 0.1)" },
};

const DateSelect = ({
  value,
  onChange,
  label,
  placeholder,
  minDate,
  disabled = false,
  error = false,
  helperText,
  required = false,
}) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const intl = useIntl();
  const locale = momentLocale(i18nLocale);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const selected = useMemo(
    () => (value ? moment(value, "YYYY-MM-DD").locale(locale) : null),
    [value, locale]
  );
  const min = useMemo(
    () => (minDate ? moment(minDate, "YYYY-MM-DD").startOf("day") : null),
    [minDate]
  );

  const [viewDate, setViewDate] = useState(
    () => (value ? moment(value, "YYYY-MM-DD") : moment()).startOf("month")
  );

  useEffect(() => {
    if (value) setViewDate(moment(value, "YYYY-MM-DD").startOf("month"));
  }, [value]);

  const weekdays = useMemo(() => {
    const ld = moment().locale(locale).localeData();
    const short = ld.weekdaysMin();
    // Reorder to a Monday-first week.
    return [...short.slice(1), short[0]];
  }, [locale]);

  const days = useMemo(() => {
    const startOfMonth = viewDate.clone().startOf("month");
    const offset = (startOfMonth.day() + 6) % 7; // Monday-first
    const gridStart = startOfMonth.clone().subtract(offset, "days");
    return [...Array(42)].map((_, i) => gridStart.clone().add(i, "days"));
  }, [viewDate]);

  const today = moment().startOf("day");

  const isDisabledDay = (d) => min && d.clone().startOf("day").isBefore(min);

  const handleSelect = (d) => {
    if (isDisabledDay(d)) return;
    onChange(d.format("YYYY-MM-DD"));
    setOpen(false);
  };

  const displayText = selected
    ? selected.format("ll")
    : placeholder || intl.formatMessage({ id: "select" });

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Typography
          sx={{
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            fontSize: "0.875rem",
            color: "hsl(178, 93%, 10%)",
            mb: "6px",
          }}
        >
          {label}
          {required && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{ color: "hsl(0, 72%, 51%)", ml: "2px" }}
            >
              *
            </Box>
          )}
        </Typography>
      )}

      <Box
        component="button"
        ref={anchorRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          height: "52px",
          px: "16px",
          textAlign: "left",
          boxSizing: "border-box",
          cursor: "pointer",
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: `1px solid ${error ? "hsl(0, 72%, 51%)" : "hsl(160, 10%, 90%)"}`,
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          fontSize: "1rem",
          color: selected ? "hsl(178, 93%, 10%)" : "hsl(178, 10%, 45%)",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:hover": { borderColor: "hsl(153, 68%, 25%)" },
          "&:focus-visible": {
            outline: "none",
            borderColor: "hsl(153, 68%, 25%)",
            boxShadow: "0 0 0 3px hsla(153, 68%, 25%, 0.3)",
          },
          "&:disabled": { opacity: 0.5, pointerEvents: "none" },
        }}
      >
        <Calendar size={18} style={{ color: "hsl(178, 10%, 45%)", flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textTransform: "capitalize",
          }}
        >
          {displayText}
        </Box>
      </Box>

      {error && helperText && (
        <Typography
          sx={{
            mt: "4px",
            ml: "2px",
            fontSize: "0.75rem",
            color: "hsl(0, 72%, 51%)",
            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          }}
        >
          {helperText}
        </Typography>
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 6] } },
          {
            name: "flip",
            enabled: true,
            options: { fallbackPlacements: ["top-start", "bottom-end", "top-end"] },
          },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener
          onClickAway={(e) => {
            if (anchorRef.current && anchorRef.current.contains(e.target)) return;
            setOpen(false);
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "280px",
              maxWidth: "calc(100vw - 2rem)",
              p: "16px",
              borderRadius: "12px",
              border: "1px solid hsl(160, 10%, 90%)",
              boxShadow:
                "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
            }}
          >
            {/* Month navigation */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: "12px",
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setViewDate(viewDate.clone().subtract(1, "month"))}
                sx={navBtnSx}
              >
                <ChevronLeft size={18} />
              </Box>
              <Box
                sx={{
                  fontSize: "0.9375rem",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  color: "hsl(178, 93%, 10%)",
                  textTransform: "capitalize",
                }}
              >
                {viewDate.clone().locale(locale).format("MMMM YYYY")}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setViewDate(viewDate.clone().add(1, "month"))}
                sx={navBtnSx}
              >
                <ChevronRight size={18} />
              </Box>
            </Box>

            {/* Weekday headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "2px",
                mb: "4px",
              }}
            >
              {weekdays.map((d, i) => (
                <Box
                  key={`${d}-${i}`}
                  sx={{
                    textAlign: "center",
                    fontSize: "0.6875rem",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    color: "hsl(178, 10%, 45%)",
                    py: "4px",
                    textTransform: "capitalize",
                  }}
                >
                  {d}
                </Box>
              ))}
            </Box>

            {/* Day grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "2px",
              }}
            >
              {days.map((d, idx) => {
                const inMonth = d.month() === viewDate.month();
                const isToday = d.isSame(today, "day");
                const isSelected = selected && d.isSame(selected, "day");
                const dayDisabled = isDisabledDay(d);
                return (
                  <Box
                    key={idx}
                    component="button"
                    type="button"
                    disabled={dayDisabled}
                    onClick={() => handleSelect(d)}
                    sx={{
                      height: "34px",
                      borderRadius: "8px",
                      border: "none",
                      cursor: dayDisabled ? "not-allowed" : "pointer",
                      fontSize: "0.8125rem",
                      fontFamily: isToday
                        ? "Segoe UI Bold, system-ui, sans-serif"
                        : "Segoe UI Regular, system-ui, sans-serif",
                      backgroundColor: isSelected
                        ? "hsl(153, 68%, 25%)"
                        : "transparent",
                      color: isSelected
                        ? "#fff"
                        : dayDisabled
                          ? "hsl(160, 10%, 80%)"
                          : inMonth
                            ? "hsl(178, 93%, 10%)"
                            : "hsl(178, 10%, 60%)",
                      transition: "background-color 0.2s, color 0.2s",
                      "&:hover":
                        isSelected || dayDisabled
                          ? {}
                          : { backgroundColor: "hsla(153, 68%, 25%, 0.1)" },
                    }}
                  >
                    {d.date()}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default DateSelect;
